import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

import { Checkbox } from '../../../components/ui/Checkbox';

const DEFAULT_STATUS_META = {
  Lead:      { color: 'bg-blue-500/10 text-blue-600 border-blue-200', dot: 'bg-blue-500' },
  Active:    { color: 'bg-emerald-500/10 text-emerald-600 border-emerald-200', dot: 'bg-emerald-500' },
  Returning: { color: 'bg-violet-500/10 text-violet-600 border-violet-200', dot: 'bg-violet-500' },
  VIP:       { color: 'bg-amber-500/10 text-amber-600 border-amber-200', dot: 'bg-amber-500' },
  Inactive:  { color: 'bg-slate-400/10 text-slate-500 border-slate-200', dot: 'bg-slate-400' },
  Blocked:   { color: 'bg-red-500/10 text-red-600 border-red-200', dot: 'bg-red-500' },
};

const ContactsTable = ({
  contacts,
  selectedContacts,
  onSelectContact,
  onSelectAllContacts,
  onContactClick,
  sortConfig,
  onSort,
  onDelete,
  statusMeta = DEFAULT_STATUS_META,
}) => {
  const [hoveredRow, setHoveredRow] = useState(null);

  const getSortIcon = (column) => {
    if (sortConfig?.key !== column) return 'ArrowUpDown';
    return sortConfig?.direction === 'asc' ? 'ArrowUp' : 'ArrowDown';
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name?.split(' ')?.map(n => n?.[0])?.join('')?.toUpperCase()?.slice(0, 2);
  };

  const AVATAR_PALETTES = [
    'bg-blue-100 text-blue-700',
    'bg-rose-100 text-rose-700',
    'bg-emerald-100 text-emerald-700',
    'bg-violet-100 text-violet-700',
    'bg-amber-100 text-amber-700',
    'bg-cyan-100 text-cyan-700',
    'bg-pink-100 text-pink-700',
    'bg-indigo-100 text-indigo-700',
  ];

  const getAvatarColor = (name) => {
    const idx = (name?.charCodeAt(0) || 0) % AVATAR_PALETTES?.length;
    return AVATAR_PALETTES?.[idx];
  };

  const formatLastContact = (date) => {
    if (!date) return '—';
    const now = new Date();
    const contactDate = new Date(date);
    const diffDays = Math.ceil(Math.abs(now - contactDate) / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    if (diffDays < 30) return `${Math.ceil(diffDays / 7)}w ago`;
    return contactDate?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const getStatusStyle = (status) => {
    return statusMeta?.[status] || { color: 'bg-muted text-muted-foreground border-border', dot: 'bg-muted-foreground' };
  };

  const handleDelete = (e, contact) => {
    e?.stopPropagation();
    if (onDelete) onDelete(contact?.id);
  };

  const columns = [
    { key: 'name', label: 'Contact' },
    { key: 'title', label: 'Title' },
    { key: 'company', label: 'Company' },
    { key: 'email', label: 'Email' },
    { key: 'phone', label: 'Phone' },
    { key: 'lastContact', label: 'Updated' },
    { key: 'client_status', label: 'Status' },
  ];

  return (
    <div className="bg-card rounded-xl border border-border overflow-hidden mb-4">
      {/* Desktop Table */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border bg-muted/40">
              <th className="w-12 px-4 py-3">
                <Checkbox
                  checked={selectedContacts?.length === contacts?.length && contacts?.length > 0}
                  indeterminate={selectedContacts?.length > 0 && selectedContacts?.length < contacts?.length}
                  onChange={(e) => onSelectAllContacts(e?.target?.checked)}
                />
              </th>
              {columns?.map((column) => (
                <th
                  key={column?.key}
                  className="px-4 py-3 text-left text-[11px] font-semibold text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground transition-colors select-none"
                  onClick={() => onSort?.(column?.key)}
                >
                  <div className="flex items-center gap-1.5">
                    <span>{column?.label}</span>
                    <Icon name={getSortIcon(column?.key)} size={12} className="opacity-40" />
                  </div>
                </th>
              ))}
              <th className="w-24 px-4 py-3 text-center text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border/60">
            {contacts?.map((contact) => {
              const statusStyle = getStatusStyle(contact?.client_status);
              return (
                <tr
                  key={contact?.id}
                  className="hover:bg-muted/30 cursor-pointer transition-colors group"
                  onClick={() => onContactClick?.(contact)}
                  onMouseEnter={() => setHoveredRow(contact?.id)}
                  onMouseLeave={() => setHoveredRow(null)}
                >
                  <td className="px-4 py-3.5" onClick={e => e?.stopPropagation()}>
                    <Checkbox
                      checked={selectedContacts?.includes(contact?.id)}
                      onChange={(e) => { e?.stopPropagation(); onSelectContact?.(contact?.id, e?.target?.checked); }}
                    />
                  </td>
                  <td className="px-4 py-3.5">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold ${getAvatarColor(contact?.name)}`}>
                        {getInitials(contact?.name)}
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-foreground leading-tight">{contact?.name}</div>
                        {contact?.department && <div className="text-xs text-muted-foreground">{contact?.department}</div>}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3.5 text-sm text-muted-foreground">{contact?.title || '—'}</td>
                  <td className="px-4 py-3.5 text-sm text-foreground">{contact?.company || '—'}</td>
                  <td className="px-4 py-3.5">
                    {contact?.email ? (
                      <span className="text-sm text-primary hover:underline">{contact?.email}</span>
                    ) : <span className="text-sm text-muted-foreground">—</span>}
                  </td>
                  <td className="px-4 py-3.5 text-sm text-foreground">{contact?.phone || '—'}</td>
                  <td className="px-4 py-3.5 text-xs text-muted-foreground">{formatLastContact(contact?.lastContact)}</td>
                  <td className="px-4 py-3.5">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${statusStyle?.color}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${statusStyle?.dot}`} />
                      {contact?.client_status || 'Lead'}
                    </span>
                  </td>
                  <td className="px-4 py-3.5">
                    <div className={`flex items-center justify-center gap-0.5 transition-opacity ${hoveredRow === contact?.id ? 'opacity-100' : 'opacity-0'}`}>
                      <button
                        className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                        onClick={(e) => { e?.stopPropagation(); window.location.href = `mailto:${contact?.email}`; }}
                        title="Send email"
                      >
                        <Icon name="Mail" size={13} />
                      </button>
                      <button
                        className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                        onClick={(e) => { e?.stopPropagation(); window.location.href = `tel:${contact?.phone}`; }}
                        title="Call"
                      >
                        <Icon name="Phone" size={13} />
                      </button>
                      <button
                        className="p-1.5 rounded-lg hover:bg-red-50 text-muted-foreground hover:text-red-600 transition-colors"
                        onClick={(e) => handleDelete(e, contact)}
                        title="Delete"
                      >
                        <Icon name="Trash2" size={13} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {contacts?.length === 0 && (
          <div className="text-center py-16">
            <div className="w-14 h-14 bg-muted rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Icon name="Users" size={24} className="text-muted-foreground" />
            </div>
            <p className="text-sm font-medium text-foreground mb-1">No contacts found</p>
            <p className="text-xs text-muted-foreground">Try adjusting your search or filters</p>
          </div>
        )}
      </div>

      {/* Mobile Cards */}
      <div className="lg:hidden">
        {contacts?.map((contact) => {
          const statusStyle = getStatusStyle(contact?.client_status);
          return (
            <div
              key={contact?.id}
              className="p-4 border-b border-border last:border-b-0 cursor-pointer hover:bg-muted/30 transition-colors"
              onClick={() => onContactClick?.(contact)}
            >
              <div className="flex items-start gap-3">
                <div onClick={e => e?.stopPropagation()} className="mt-0.5">
                  <Checkbox
                    checked={selectedContacts?.includes(contact?.id)}
                    onChange={(e) => { e?.stopPropagation(); onSelectContact?.(contact?.id, e?.target?.checked); }}
                  />
                </div>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold ${getAvatarColor(contact?.name)}`}>
                  {getInitials(contact?.name)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <h3 className="text-sm font-semibold text-foreground truncate">{contact?.name}</h3>
                      <p className="text-xs text-muted-foreground">{contact?.title || contact?.company || ''}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${statusStyle?.color}`}>
                        <span className={`w-1 h-1 rounded-full ${statusStyle?.dot}`} />
                        {contact?.client_status || 'Lead'}
                      </span>
                      <button
                        className="p-1 rounded-lg hover:bg-red-50 text-muted-foreground hover:text-red-600 transition-colors"
                        onClick={(e) => handleDelete(e, contact)}
                      >
                        <Icon name="Trash2" size={13} />
                      </button>
                    </div>
                  </div>
                  <div className="mt-2 space-y-1">
                    {contact?.email && (
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Icon name="Mail" size={12} />
                        <span className="truncate">{contact?.email}</span>
                      </div>
                    )}
                    {contact?.phone && (
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Icon name="Phone" size={12} />
                        <span>{contact?.phone}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
        {contacts?.length === 0 && (
          <div className="text-center py-16">
            <div className="w-14 h-14 bg-muted rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Icon name="Users" size={24} className="text-muted-foreground" />
            </div>
            <p className="text-sm font-medium text-foreground mb-1">No contacts found</p>
            <p className="text-xs text-muted-foreground">Try adjusting your search or filters</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContactsTable;